import { createRouter, createWebHistory } from 'vue-router'

import { requirePermissions, sessionGuard } from './guards.js'
import { routes } from './routes.js'

/**
 * The router.
 *
 * Two guard tiers, registered in one place: the global `beforeEach` classifies the session,
 * and `requirePermissions` is attached to **every** route here rather than route by route.
 * Attaching it centrally is the difference between a permission check that is enforced and
 * one that a new route forgets — the route still declares *what* it requires in its own
 * `meta.requires`, which is the part that belongs beside the path.
 */
export function createAppRouter(): ReturnType<typeof createRouter> {
  const router = createRouter({
    history: createWebHistory(),
    routes: [...routes],

    /**
     * Restore on back, top on forward.
     *
     * An operator paging through a long shop list and clicking into a shop expects the list
     * exactly where they left it when they come back. `savedPosition` is only present for a
     * history traversal, which is precisely that case.
     */
    scrollBehavior: (_to, _from, savedPosition) => savedPosition ?? { top: 0 },
  })

  router.beforeEach(sessionGuard())
  router.beforeEach(requirePermissions())

  /**
   * The title, after the navigation is confirmed.
   *
   * `afterEach`, not `beforeEach`: setting it before confirmation renames the tab for a
   * navigation that a guard then cancels, leaving the title describing a page the operator
   * never reached.
   */
  router.afterEach((to) => {
    const title = typeof to.meta.title === 'string' ? to.meta.title : null

    document.title = title === null ? 'Zapio Operator' : `${title} · Zapio Operator`
  })

  return router
}
